import {
  useContext,
  useMemo,
  useState,
  createContext,
  useEffect,
  useCallback,
} from 'react';
const FormStateContext = createContext({});
FormStateContext.displayName = 'FormStateContext';
export const FormStateProvider = ({ children }) => {
  const [orderFields, setOrderFields] = useState({});
  const [enabledContinueBtn, setEnabledContinueBtn] = useState(true);
  const isFormInvalid = Object.keys(orderFields).some((stop) =>
    Object.keys(orderFields[stop]).some(
      (field) => !orderFields[stop][field].isValid
    )
  );
  const newStop = Object.keys(orderFields).some((stop) =>
    Object.keys(orderFields[stop]).some(
      (field) => !orderFields[stop][field].showError
    )
  );

  useEffect(() => {
    setEnabledContinueBtn(!isFormInvalid || newStop);
  }, [isFormInvalid, newStop]);

  const validateForm = useCallback(() => {
    Object.keys(orderFields).forEach((stop) =>
      Object.keys(orderFields[stop]).forEach((field) =>
        setOrderFields((prev) => {
          if (!prev[stop]) return prev;
          return {
            ...prev,
            [stop]: {
              ...prev[stop],
              [field]: {
                ...prev[stop][field],
                showError: true,
              },
            },
          };
        })
      )
    );
  }, [orderFields]);

  const validateField = useCallback(
    (stop, field, value) => {
      setOrderFields((prev) => {
        if (!prev[stop]) return prev;
        return {
          ...prev,
          [stop]: {
            ...prev[stop],
            [field]: {
              ...prev[stop][field],
              isValid: !!value,
            },
          },
        };
      });
    },
    [setOrderFields]
  );

  const removeStop = useCallback(
    (stop, field) => {
      setOrderFields((prev) => {
        const temp = { ...prev };
        delete temp[stop][field];
        if (Object.keys(temp[stop]).length === 0) {
          delete temp[stop];
        }
        return temp;
      });
    },
    [setOrderFields]
  );

  const addStop = useCallback(
    (stop, field) => {
      setOrderFields((prev) => ({
        ...prev,
        [stop]: {
          ...prev[stop],
          [field]: {
            showError: false,
            isValid: false,
          },
        },
      }));
    },
    [setOrderFields]
  );

  const formFunctions = useMemo(
    () => ({
      addStop,
      removeStop,
      validateField,
      validateForm,
      setOrderFields,
      orderFields,
      enabledContinueBtn,
      isFormInvalid,
    }),
    [
      addStop,
      removeStop,
      validateField,
      validateForm,
      setOrderFields,
      orderFields,
      enabledContinueBtn,
      isFormInvalid,
    ]
  );

  return (
    <FormStateContext.Provider value={formFunctions}>
      {children}
    </FormStateContext.Provider>
  );
};

const useFormState = (stop, field, value) => {
  const { orderFields, validateField, addStop, removeStop, ...priceFunctions } =
    useContext(FormStateContext);

  useEffect(() => {
    if (!stop) return;
    addStop(stop, field);
    return () => removeStop(stop, field);
  }, [stop, field, addStop, removeStop]);

  useEffect(() => {
    if (!stop) return;
    validateField(stop, field, value);
  }, [stop, field, value, validateField]);

  const fieldHasError = useMemo(() => {
    if (orderFields[stop] && orderFields[stop][field]) {
      const error =
        !orderFields[stop][field].isValid && orderFields[stop][field].showError;
      return error ? 'error' : '';
    }
  }, [orderFields, field, stop]);

  return {
    fieldHasError,
    ...priceFunctions,
  };
};

export default useFormState;
